import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, Tenant, AuthResponse } from '../types';
import { apiService } from '../services/api';
import { upsertFromActiveStorage } from '../utils/accountSwitcher';

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  updateTenant: (tenant: Tenant) => void;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; tenant: Tenant; token: string } }
  | { type: 'AUTH_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'UPDATE_TENANT'; payload: Tenant };

const initialState: AuthState = {
  user: null,
  tenant: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tenant: action.payload.tenant,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        tenant: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        tenant: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'UPDATE_TENANT':
      return {
        ...state,
        tenant: action.payload,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing token on app load and refresh profile (seniorBranchIds)
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    const tenantStr = localStorage.getItem('tenant');

    if (token && userStr && tenantStr) {
      try {
        const user = JSON.parse(userStr);
        const tenant = JSON.parse(tenantStr);
        
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user, tenant, token }
        });

        apiService
          .getProfile()
          .then((profile) => {
            if (!profile) return;
            const nextUser: User = {
              ...user,
              ...profile,
              tenantId: profile.tenantId || user.tenantId,
              isSeniorTrainer: Boolean(profile.isSeniorTrainer),
              seniorBranchIds: profile.seniorBranchIds || [],
              trainerId:
                profile.trainerId ??
                (profile.trainer?.isActive !== false ? profile.trainer?.id : null) ??
                null,
              isTrainer: Boolean(
                profile.trainerId ??
                  (profile.trainer?.isActive !== false ? profile.trainer?.id : null)
              ),
            };
            localStorage.setItem('user', JSON.stringify(nextUser));
            dispatch({ type: 'UPDATE_USER', payload: nextUser });
            if (profile.tenant) {
              const nextTenant = { ...tenant, ...profile.tenant };
              localStorage.setItem('tenant', JSON.stringify(nextTenant));
              dispatch({ type: 'UPDATE_TENANT', payload: nextTenant });
            }
          })
          .catch(() => {
            /* keep cached session */
          });
      } catch (error) {
        console.error('Error parsing stored auth data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenant');
        dispatch({ type: 'AUTH_FAILURE' });
      }
    } else {
      dispatch({ type: 'AUTH_FAILURE' });
    }
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      const response: AuthResponse = await apiService.login({ email, password });
      
      // Store in localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('tenant', JSON.stringify(response.tenant));
      upsertFromActiveStorage();

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: response.user,
          tenant: response.tenant,
          token: response.token,
        },
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const register = async (data: any): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      const response: AuthResponse = await apiService.register(data);
      
      // Store in localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('tenant', JSON.stringify(response.tenant));
      upsertFromActiveStorage();

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: response.user,
          tenant: response.tenant,
          token: response.token,
        },
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const logout = (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = (user: User): void => {
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const updateTenant = (tenant: Tenant): void => {
    localStorage.setItem('tenant', JSON.stringify(tenant));
    dispatch({ type: 'UPDATE_TENANT', payload: tenant });
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    updateTenant,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
